import { api } from 'API';

const baseUrl = '/CaseDetails';

export interface MilitaryServiceSection {
  militaryID?: string | null;
  releaseDate?: string | null;
  serviceTypeId?: string | null;
  otherServiceType?: string | null;
  medicalProfile?: string | null;
  role?: string | null;
  rankId?: string | null;
  troopId?: string | null;
  unitAtTheTimeOfInjury?: string | null;
  hasNoTroop?: boolean;
  hasNoReleaseDate?: boolean;
  hasNoMedicalProfile?: boolean;
}

interface ICaseTypeSection {
  isInjury?: boolean;
  isDisease?: boolean;
  isPostTrauma?: boolean;
  isMentalIllness?: boolean;
}

export interface InjuryKindSection {
  date?: string | null;
  description?: string | null;
  reportAttachedSelectedOptionId?: string | null;
  isInjuryReportFileClassified?: boolean | null;
  investigationHasBeen?: boolean | null;
  investigatorOptionId?: string | null;
  hasNoInvestigator?: boolean | null;
  hasNoInvestigationReportFile?: boolean | null;
  whenInjuryHappanedOptionId?: string | null;
  militaryBaseLocation?: string | null;
  residentiaAddress?: string | null;
  injuryLocation?: string | null;
  isStopedOnIntermediateDest?: boolean | null;
  stopPurpose?: string | null;
  isInjuryDescribedInFile?: boolean | null;
}

export interface Witness {
  id?: number | null;
  idNumber?: string | null;
  personalMilitaryID?: string | null;
  civilAddress?: string | null;
  militaryMail?: string | null;
  role?: string | null;
  phonePrefixRelationId?: string | null;
  phone?: string | null;
  typeId?: string | null;
  otherWitnessesType?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  isClassifiedRole?: boolean | null;
  hasNoCivilAddress?: boolean | null;
  hasNoWitnessDocument?: boolean | null;
}

export interface VehicleDetailsSection {
  ownershipVehicleId?: string | null;
  vehicleId?: string | null;
}

export interface TrafficAccidentSection {
  vehicleDetailsSection?: VehicleDetailsSection;
  anotherVehicleInvolvedAccident?: boolean | null;
  insuranceCompany?: string;
  whatHaveYouDoDuringAccidentOptionId?: number | null;
  isDrivingLicenseExist?: boolean | null;
  isVehicleInsuranceExist?: boolean | null;
  compensationClaimSentOptionId?: string | null;
  isPoliceReportExist?: boolean | null;
  isPoliceReportFileClassified?: boolean | null;
}

export interface DiseaseKindSection {
  diseaseDiagnosisDate?: string;
  diseaseDetailsDescription?: string;
  isRelativesWithSameDisease?: boolean | null;
  isDiagnosisExist?: boolean | null;
  relativesWithSameDisease?: string;
  diseaseOptionId?: string | null;
  isDiseaseDescribedInFile?: boolean | null;
  relativeRelation?: string | null;
}

export interface HospitalizationDetailsSection {
  id?: number;
  medicalInstitutionName?: string;
  clinicDepartment?: string;
  hospitalization?: string;
  hospitalizationStartDate?: string | null;
  hospitalizationEndDate?: string | null;
  doctorName?: string;
  wasHospitalized?: boolean | null;
  hasNoHospitalizationStartDate?: boolean | null;
  hasNoHospitalizationEndDate?: boolean | null;
  hasNoClinicDepartment?: boolean | null;
  hasNoDoctorName?: boolean | null;
  description?: string | null;
}

export interface ICommanderDetailsSection {
  firstName?: string | null;
  lastName?: string | null;
  phonePrefixRelationId?: string | null;
  phoneNumber?: string | null;
  civilAddress?: string | null;
  hasNoCivilAddress?: boolean | null;
  hasNoCommanderDocument?: boolean | null;
  isAlsoWitness?: boolean | null;
}

export interface ICaseDetails {
  id: number;
  isClassifiedDocsApproved?: boolean | null;
  militaryServiceSection?: MilitaryServiceSection;
  caseTypeSection?: ICaseTypeSection;
  caseTypeRelationId?: string;
  injuryKindSection?: InjuryKindSection;
  diseaseKindSection?: DiseaseKindSection;
  isTrafficAccident?: boolean | null;
  trafficAccidentSection?: TrafficAccidentSection;
  commanderDetailsSection?: ICommanderDetailsSection;
  areThereWitnessesOptionId?: number | null;
  witnesses?: Witness[];
  wasMedicalInstitutionTreatment?: boolean | null;
  hospitalizationDetailsSection?: HospitalizationDetailsSection[];
  isCausedBySpecificEvent?: boolean | null;
  areThereOtherWitnesses?: boolean | null;
  otherWitnessesDetails?: string | null;
  areThereMedicalDocuments?: boolean | null;
  hasNoHmoFile?: boolean | null;
  isPrevHmoExist?: boolean | null;
  hasNoPrevHmoFile?: boolean | null;
  areOtherRelevantFiles?: boolean | null;
}

export interface ICaseDetailsInputs {
  caseDetails: ICaseDetails[];
}
export const witnessesEmptyObject: Witness = {
  id: undefined,
  civilAddress: undefined,
  firstName: undefined,
  idNumber: undefined,
  lastName: undefined,
  militaryMail: undefined,
  otherWitnessesType: undefined,
  personalMilitaryID: undefined,
  phone: undefined,
  phonePrefixRelationId: undefined,
  role: undefined,
  typeId: undefined,
  isClassifiedRole: undefined,
  hasNoCivilAddress: undefined,
  hasNoWitnessDocument: undefined,
};

export const hospitalizationDetailsSectionEmptyObject: HospitalizationDetailsSection =
  {
    doctorName: undefined,
    clinicDepartment: undefined,
    hospitalization: undefined,
    hospitalizationEndDate: undefined,
    hospitalizationStartDate: undefined,
    medicalInstitutionName: undefined,
    wasHospitalized: undefined,
    hasNoHospitalizationStartDate: undefined,
    hasNoHospitalizationEndDate: undefined,
    hasNoClinicDepartment: undefined,
    hasNoDoctorName: undefined,
    description: undefined,
  };

export const caseDetailsEmptyObject: Omit<ICaseDetails, 'id'> = {
  isClassifiedDocsApproved: undefined,
  areThereMedicalDocuments: undefined,
  areOtherRelevantFiles: undefined,
  hasNoHmoFile: undefined,
  isPrevHmoExist: undefined,
  hasNoPrevHmoFile: undefined,
  militaryServiceSection: {
    medicalProfile: undefined,
    militaryID: undefined,
    otherServiceType: undefined,
    rankId: undefined,
    releaseDate: undefined,
    role: undefined,
    serviceTypeId: undefined,
    troopId: undefined,
    unitAtTheTimeOfInjury: undefined,
    hasNoTroop: undefined,
    hasNoReleaseDate: undefined,
    hasNoMedicalProfile: undefined,
  },
  caseTypeSection: {
    isDisease: undefined,
    isInjury: undefined,
    isMentalIllness: undefined,
    isPostTrauma: undefined,
  },
  caseTypeRelationId: undefined,
  injuryKindSection: {
    date: undefined,
    description: undefined,
    investigationHasBeen: undefined,
    investigatorOptionId: undefined,
    reportAttachedSelectedOptionId: undefined,
    isInjuryReportFileClassified: undefined,
    whenInjuryHappanedOptionId: undefined,
    isInjuryDescribedInFile: undefined,
    hasNoInvestigator: undefined,
    hasNoInvestigationReportFile: undefined,
    militaryBaseLocation: undefined,
    residentiaAddress: undefined,
    injuryLocation: undefined,
    isStopedOnIntermediateDest: undefined,
    stopPurpose: undefined,
  },
  diseaseKindSection: {
    isDiagnosisExist: undefined,
    diseaseDetailsDescription: undefined,
    diseaseDiagnosisDate: undefined,
    isRelativesWithSameDisease: undefined,
    relativesWithSameDisease: undefined,
    diseaseOptionId: undefined,
    isDiseaseDescribedInFile: undefined,
    relativeRelation: undefined,
  },
  isTrafficAccident: undefined,
  trafficAccidentSection: {
    isPoliceReportExist: undefined,
    isPoliceReportFileClassified: undefined,
    isVehicleInsuranceExist: undefined,
    isDrivingLicenseExist: undefined,
    compensationClaimSentOptionId: undefined,
    whatHaveYouDoDuringAccidentOptionId: undefined,
    anotherVehicleInvolvedAccident: undefined,
    insuranceCompany: undefined,
    vehicleDetailsSection: {
      ownershipVehicleId: undefined,
      vehicleId: undefined,
    },
  },
  commanderDetailsSection: {
    firstName: undefined,
    lastName: undefined,
    phonePrefixRelationId: undefined,
    phoneNumber: undefined,
    civilAddress: undefined,
    hasNoCivilAddress: undefined,
    hasNoCommanderDocument: undefined,
    isAlsoWitness: undefined,
  },
  areThereWitnessesOptionId: undefined,
  witnesses: [
    {
      ...witnessesEmptyObject,
    },
  ],
  wasMedicalInstitutionTreatment: undefined,
  hospitalizationDetailsSection: [
    {
      ...hospitalizationDetailsSectionEmptyObject,
    },
  ],
  isCausedBySpecificEvent: undefined,
  areThereOtherWitnesses: undefined,
  otherWitnessesDetails: undefined,
};

interface ICaseDetailsWitnessId {
  id: number;
}

export const getCaseDetails = async () => {
  const caseDetails = await api.get<ICaseDetails[]>(baseUrl);
  if (caseDetails.data) {
    return caseDetails.data;
  }
};

export const putCaseDetails = async (data: Partial<ICaseDetails>[]) => {
  const caseDetails = await api.put(`${baseUrl}`, data);
  if (caseDetails.status === 200) return;
};

export const postCaseDetails = async (data: Partial<ICaseDetails>) => {
  const caseDetailsData = await api.post<ICaseDetails>(baseUrl, data);
  if (caseDetailsData.data) {
    return caseDetailsData.data;
  }
};

export const deleteCaseDetails = async (id: number) => {
  const caseDetailsData = await api.delete(baseUrl + '/' + id);
  if (caseDetailsData.status === 200) {
    return true;
  }
  return false;
};

export const postHospitalization = async (caseDetailsId: number) => {
  const hospitalization = await api.post<HospitalizationDetailsSection>(
    baseUrl + '/' + caseDetailsId + '/hospitalizationDetails',
    {}
  );
  if (hospitalization.data) {
    return hospitalization.data;
  }
};

export const deleteHospitalization = async (
  caseDetailsId: number,
  hospitalizationId: number
) => {
  const hospitalization = await api.delete(
    baseUrl +
      '/' +
      caseDetailsId +
      '/hospitalizationDetails/' +
      hospitalizationId
  );
  if (hospitalization.status === 200) {
    return true;
  }
  return false;
};

export const createNewWitness = async (caseDetailsId: number) => {
  const caseDetailsWitnessId = await api.post<ICaseDetailsWitnessId>(
    `${baseUrl}/${caseDetailsId}/witnesses`,
    {}
  );
  if (caseDetailsWitnessId.data) return caseDetailsWitnessId.data.id;
};

export const deleteWitness = async (witnessId: number) => {
  await api.delete<any>(`${baseUrl}/witnesses/${witnessId}`);
};
